import React, { useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';

import {
  Header,
  SearchHeader,
  Option,
  SearchByDateRange,
  SearchByText,
  UsersDropdown,
  RegionDropdown,
  CityDropdown,
} from '../../components';
import { spacing, colors } from '../../constants/theme';
import { EventFragment } from '../../generated/EventFragment';
import { EventsVariables } from '../../generated/Events';

import DboNewsEventTable from './DboNewsEventTable';

type Props = {
  refetchToggle?: boolean;
  hideEdit?: boolean;
  hideArchiveCheck?: boolean;
  queryVariables?: EventsVariables;
  setEditEvent?: (value: EventFragment) => void;
};

const DboNewsSearchEvent = (props: Props) => {
  let {
    refetchToggle,
    hideEdit,
    hideArchiveCheck,
    queryVariables,
    setEditEvent,
  } = props;
  // NOTE: these are used for filter state
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Option | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Option | null>(null);
  const [selectedCity, setSelectedCity] = useState<Option | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateUntil, setDateUntil] = useState<Date | null>(null);
  const [inputEvent, setInputEvent] = useState<string | null>(null);

  // NOTE: these are used to filter the queries
  const [searchArchived, setSearchArchived] = useState(false);
  const [searchByUser, setSearchByUser] = useState<string | null>(null);
  const [searchByCity, setSearchByCity] = useState<string | null>(null);
  const [searchByRegion, setSearchByRegion] = useState<string | null>(null);
  const [searchByDateFrom, setSearchByDateFrom] = useState<Date | null>(null);
  const [searchByDateUntil, setSearchByDateUntil] = useState<Date | null>(null);
  const [searchByEvent, setSearchByEvent] = useState<string | null>(null);
  const [searchDisabled, setSearchDisabled] = useState(false);

  const clearSearch = useCallback(() => {
    setIncludeArchived(false);
    setSelectedUser(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setDateFrom(null);
    setDateUntil(null);
    setInputEvent(null);
  }, [
    setIncludeArchived,
    setSelectedUser,
    setSelectedRegion,
    setSelectedCity,
    setDateFrom,
    setDateUntil,
    setInputEvent,
  ]);

  const submitSearch = useCallback(() => {
    setSearchArchived(includeArchived);
    setSearchByUser(selectedUser?.value.toString() ?? null);
    setSearchByRegion(selectedRegion ? selectedRegion.label : null);
    setSearchByCity(selectedCity ? selectedCity.label : null);
    if (dateFrom) {
      setSearchByDateFrom(new Date(dateFrom.setHours(0, 0, 0)));
    } else {
      setSearchByDateFrom(null);
    }
    if (dateUntil) {
      setSearchByDateUntil(new Date(dateUntil.setHours(23, 59, 59)));
    } else {
      setSearchByDateUntil(null);
    }
    setSearchByEvent(inputEvent);
  }, [
    setSearchArchived,
    setSearchByUser,
    setSearchByCity,
    setSearchByDateFrom,
    setSearchByDateUntil,
    setSearchByEvent,
    includeArchived,
    selectedUser,
    selectedRegion,
    selectedCity,
    dateFrom,
    dateUntil,
    inputEvent,
  ]);

  const onSelectRegion = useCallback((selected: Option) => {
    setSelectedRegion(selected);
    setSelectedCity(null);
  }, []);

  return (
    <>
      <SearchHeader
        data-testid="search-event"
        archivable={!hideArchiveCheck}
        title={t(['Event', 'Events'])}
        includeArchived={includeArchived}
        onArchivePress={setIncludeArchived}
        onSearchPress={submitSearch}
        onClearPress={clearSearch}
        disabled={searchDisabled}
      >
        <View>
          <UsersDropdown
            data-testid="search-user-dropdown"
            type="side"
            title={t(['Cari Pengguna', 'Search by Users'])}
            selectedOption={selectedUser?.value}
            onSelect={setSelectedUser}
            style={styles.width65}
          />
          <View style={styles.divider} />
        </View>
        <View>
          <RegionDropdown
            data-testid="search-region-dropdown"
            type="side"
            title={t(['Cari Provinsi', 'Search by Region'])}
            selectedOption={selectedRegion?.value}
            onSelect={onSelectRegion}
            style={styles.width65}
          />
          <View style={styles.divider} />
        </View>
        <View>
          <CityDropdown
            data-testid="search-city-dropdown"
            type="side"
            provincesID={selectedRegion ? [selectedRegion.value] : []}
            title={t(['Cari Kota', 'Search by City'])}
            selectedOption={selectedCity?.value}
            onSelect={setSelectedCity}
            style={styles.width65}
          />
          <View style={styles.divider} />
        </View>
        <View style={styles.separator}>
          <SearchByDateRange
            data-testid="search-event-date"
            from={dateFrom}
            until={dateUntil}
            setFrom={setDateFrom}
            setUntil={setDateUntil}
            setDisabled={setSearchDisabled}
          />
        </View>
        <View style={styles.separator}>
          <SearchByText
            data-testid="search-event-name"
            label={t(['Cari Event', 'Search by Event'])}
            value={inputEvent ?? ''}
            setValue={setInputEvent}
          />
        </View>
        <View style={styles.smallBottomSpacing} />
      </SearchHeader>
      <Header
        withSeparator
        title="Results"
        titleColor="link"
        infotipContent={t([
          'Seluruh data Event akan ditampilkan pada daftar berikut. Klik Judul Kolom untuk penyortiran berdasarkan judul kolom yang di kilik. Klik tulisan “Lihat” untuk melihat detail konten judul tersebut. Klik “Icon” Arsip untuk mengirimkan data ke halaman folder arsip. Data yang sudah berada di Folder Arsip tidak akan nampak pada daftar ini, Anda dapat mengembalikan ke daftar ini dengan cara masuk pada menu “Halaman Folder Arsip”.',
          'All Event data will be displayed in the following list. Click Column Headers for sorting based on the column headings that are clicked. Click "View" to see the detailed content of the title. Click the Archive “Icon” to send the data to the archive folder page. The data that is already in the Archive Folder will not appear in this list, you can return the data to this list by going to the "Archive Folder Page" menu.',
        ])}
        style={styles.topPadding}
      />
      <DboNewsEventTable
        setEditEvent={setEditEvent}
        refetchToggle={refetchToggle}
        hideEdit={hideEdit}
        queryVariables={queryVariables}
        searchByUser={searchByUser ?? undefined}
        searchByRegion={searchByRegion ?? undefined}
        searchByCity={searchByCity ?? undefined}
        searchByEvent={searchByEvent ?? undefined}
        searchByDateFrom={searchByDateFrom ?? undefined}
        searchByDateUntil={searchByDateUntil ?? undefined}
        searchArchived={searchArchived ?? undefined}
      />
    </>
  );
};

const styles = StyleSheet.create({
  topPadding: { paddingTop: spacing.xlarge },
  smallBottomSpacing: {
    marginBottom: spacing.small,
  },
  separator: {
    paddingBottom: spacing.small,
    marginBottom: spacing.small,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.primary,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border.primary,
    marginVertical: spacing.small,
    width: '65%',
  },
  width65: { width: '65%' },
});

export default DboNewsSearchEvent;
